import React, { useEffect } from 'react';
import { Radio, RadioGroup, FormControlLabel, Checkbox } from '@mui/material'
import { Label } from 'components/Form'
import { WrapField, WrapFieldContent, CheckBoxListStyles } from './Styled';
import { removeItem } from 'utils/array';
import { isEmpty } from 'lodash';

/**
 * CheckboxList (multi-select checkbox group)
 * Renders a list of checkboxes from `options`. Supports multiple selections stored as an array.
 *
 * **How it works**
 * - Pass form state via `input`: `{ value, onChange }`.
 * - `value` is an **array** of selected option values (strings/ids).
 * - Each option: `{ id, label, value }` — `value` is what gets stored; `label` is displayed.
 * - On mount, if `value` is empty and `options` exist, the first option is auto-selected.
 *
 * Exported as `CheckboxList` from `components/Form`. Internal name: `RadioList` (legacy).
 * Uses MUI `Checkbox` inside a `RadioGroup` wrapper for layout only.
 *
 * @param {Object} props
 * @param {Object} props.input - Form binding: `{ value, onChange }`.
 * @param {Array<string|number>} props.input.value - Selected option values.
 * @param {Function} props.input.onChange - Called with the updated values array.
 * @param {Array<{id: *, label: string, value: *}>} props.options - Checkbox options.
 * @param {string} [props.label] - Group label above the list.
 * @param {boolean} [props.mandatory] - Shows required indicator on labels.
 * @param {boolean} [props.disabled] - Disables all checkboxes.
 * @param {boolean} [props.row] - Horizontal layout via MUI RadioGroup `row`.
 * @param {string} [props.name] - Group name forwarded to RadioGroup.
 * @param {string} [props.color] - MUI Checkbox color.
 * @param {string} [props.className] - CSS class on the group.
 * @param {boolean} [props.section] - Passed to `WrapField` for section styling.
 * @example
 * <CheckboxList
 *   label="Permissions"
 *   options={[{ id: 1, label: 'Read', value: 'read' }, { id: 2, label: 'Write', value: 'write' }]}
 *   input={{ value: selected, onChange: setSelected }}
 * />
 * @example
 * // In form field mapping
 * checkboxList: (props) => <CheckboxList {...props} />
 */
const RadioList = (props) => {
    const { disabled, color, row, label, name, input, options, section, className, placeholder, multiple, mandatory } = props,
        { onChange, value } = input;

    const handlePlayerToggle = (name) => {
        let nChecked = [...value]
        if (nChecked.includes(name)) {
            const index = nChecked.findIndex((item) => item == name)
            nChecked = removeItem(nChecked, index)
        }
        else nChecked.push(name)
        onChange(nChecked)
    }

    useEffect(() => {
        if (isEmpty(value) && options)
            onChange([options[0]?.value])
    }, [])

    return (
        <WrapField section={section}>
            <WrapFieldContent>
                {label && <Label label={label} mandatory={mandatory} />}
                <CheckBoxListStyles>
                    <RadioGroup
                        row={row}
                        name={name}
                        multiple={multiple}
                        className={className}
                        aria-labelledby="demo-radio-buttons-group-label"
                    >
                        {options?.map(opt => {
                            const { id, label } = opt
                            return (
                                <div className='checkbox-and-label'>
                                    <Checkbox
                                        color={color}
                                        key={id}
                                        disabled={disabled}
                                        checked={value.includes(opt.value)}
                                        onChange={(_, checked) => handlePlayerToggle(opt.value, checked)}
                                        label={label}
                                    />
                                    {label && <Label className="checkbox-label" label={label} mandatory={mandatory} />}
                                </div>
                            )
                        })}
                    </RadioGroup>
                </CheckBoxListStyles>
            </WrapFieldContent>
        </WrapField>
    );
}

export default RadioList
